import {
  Box,
  Heading,
  Link,
  Tag,
  TagLabel,
  Tooltip,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';

import type { ResolvedSkill } from '../hooks/useMapUserSkills';
import type { RoleSkill } from '../hooks/useRoleSkillComposition';
import { normalizeForMatch } from '../utils/normalizeForMatch';

interface YourSkillsPanelProps {
  skills: string[];
  resolvedSkills: ResolvedSkill[];
  unresolvedSkills: string[];
  isLoading: boolean;
  roleSkills: RoleSkill[];
}

const MAX_VISIBLE = 15;

export const YourSkillsPanel = ({
  skills,
  resolvedSkills,
  isLoading,
  roleSkills,
}: YourSkillsPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Deduplicate skills (case-insensitive), preserving first occurrence's casing
  const uniqueSkills = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const skill of skills) {
      const key = skill.toLowerCase().trim();
      if (!seen.has(key)) {
        seen.add(key);
        result.push(skill);
      }
    }
    return result;
  }, [skills]);

  // Build a lookup from resolved skill name → taxonomy info
  const resolvedLookup = useMemo(() => {
    const map = new Map<string, ResolvedSkill>();
    for (const rs of resolvedSkills) {
      map.set(rs.name.toLowerCase().trim(), rs);
    }
    return map;
  }, [resolvedSkills]);

  // Build a lookup from role skill name → share (for ranking)
  const roleSkillShareLookup = useMemo(() => {
    const map = new Map<string, number>();
    for (const rs of roleSkills) {
      map.set(normalizeForMatch(rs.shortName), rs.latestShare);
      map.set(normalizeForMatch(rs.longName), rs.latestShare);
    }
    return map;
  }, [roleSkills]);

  // Sort skills: those with role share data first (by share desc), then unmatched
  const sortedSkills = useMemo(() => {
    return [...uniqueSkills].sort((a, b) => {
      const resolvedA = resolvedLookup.get(a.toLowerCase().trim());
      const resolvedB = resolvedLookup.get(b.toLowerCase().trim());

      const shareA = resolvedA
        ? (roleSkillShareLookup.get(
            normalizeForMatch(resolvedA.taxonomyName)
          ) ?? 0)
        : 0;
      const shareB = resolvedB
        ? (roleSkillShareLookup.get(
            normalizeForMatch(resolvedB.taxonomyName)
          ) ?? 0)
        : 0;

      // Both have share → sort by share desc
      if (shareA > 0 && shareB > 0) return shareB - shareA;
      // One has share → it comes first
      if (shareA > 0) return -1;
      if (shareB > 0) return 1;
      // Both have no share → resolved ones first
      if (resolvedA && !resolvedB) return -1;
      if (!resolvedA && resolvedB) return 1;
      return 0;
    });
  }, [uniqueSkills, resolvedLookup, roleSkillShareLookup]);

  if (uniqueSkills.length === 0 && !isLoading) {
    return null;
  }

  const displaySkills = isExpanded
    ? sortedSkills
    : sortedSkills.slice(0, MAX_VISIBLE);

  return (
    <Box>
      <Heading size="xs" color="gray.600" mb={3}>
        Your Skills
      </Heading>

      <Wrap spacing={2}>
        {displaySkills.map((skill) => {
          const resolved = resolvedLookup.get(skill.toLowerCase().trim());
          const roleShare = resolved
            ? (roleSkillShareLookup.get(
                normalizeForMatch(resolved.taxonomyName)
              ) ?? null)
            : null;

          const tooltipLabel =
            roleShare !== null
              ? `${roleShare.toFixed(1)}% of people in this role have this skill`
              : undefined;

          return (
            <WrapItem key={skill}>
              <Tooltip
                label={tooltipLabel}
                fontSize="xs"
                hasArrow
                isDisabled={!tooltipLabel}
              >
                <Tag
                  size="sm"
                  height={6}
                  borderRadius="4px"
                  bg="blue.500"
                  color="white"
                >
                  <TagLabel fontWeight={600} fontSize="xs">
                    {skill}
                  </TagLabel>
                </Tag>
              </Tooltip>
            </WrapItem>
          );
        })}

        {!isExpanded && sortedSkills.length > MAX_VISIBLE && (
          <WrapItem>
            <Link
              fontSize="sm"
              color="blue.500"
              onClick={() => setIsExpanded(true)}
            >
              +{sortedSkills.length - MAX_VISIBLE} more
            </Link>
          </WrapItem>
        )}

        {isExpanded && sortedSkills.length > MAX_VISIBLE && (
          <WrapItem>
            <Link
              fontSize="sm"
              color="blue.500"
              onClick={() => setIsExpanded(false)}
            >
              Show less
            </Link>
          </WrapItem>
        )}
      </Wrap>
    </Box>
  );
};
